import axios from 'axios'
import { apiClient } from './client'
import type {
  ScubaReport,
  ScubaSummary,
  ScubaSecurityRisk,
  ScubaScanResponse,
  UserInsightsSummary,
  LicenseSkuSummary,
  UserDetailListResult,
  UserIssueFilter,
  RefreshStatusInfo,
  RefreshResponse,
  PhishResistantMfaSummary,
  UserInsightsTrendsResult,
  SecureScoreSummary,
  SecureScoreResult,
  SecureScoreControlProfile,
} from '@/types'

/**
 * Per-tenant security/analytics surfaces: SCuBA compliance scan, User
 * Insights (license/MFA/inactive-user analytics), and Microsoft Secure
 * Score. Split out of `lib/api.ts` (WO-10.4) — named `insights` rather than
 * `users` to avoid confusion with the User *Management* CRUD surface in
 * `admin.ts`.
 */

// SCuBA Compliance APIs
export async function startScubaScan(tenantId: string): Promise<ScubaScanResponse> {
  const response = await apiClient.post<ScubaScanResponse>(`/scuba/scan/tenant/${tenantId}`)
  return response.data
}

export async function getScubaReport(tenantId: string): Promise<ScubaReport | null> {
  try {
    const response = await apiClient.get<ScubaReport>(`/scuba/report/tenant/${tenantId}`)
    return response.data
  } catch (error: unknown) {
    // Return null if no report exists (404)
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

export async function getScubaRisks(tenantId: string, limit: number = 10): Promise<ScubaSecurityRisk[]> {
  try {
    const response = await apiClient.get<ScubaSecurityRisk[]>(`/scuba/risks/tenant/${tenantId}`, {
      params: { limit }
    })
    return response.data
  } catch (error: unknown) {
    // Return empty array if no report exists
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return []
    }
    throw error
  }
}

export async function getScubaSummary(tenantId: string): Promise<ScubaSummary> {
  try {
    const response = await apiClient.get<ScubaSummary>(`/scuba/summary/tenant/${tenantId}`)
    return response.data
  } catch (error: unknown) {
    // Return default empty summary if no report exists
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return {
        tenantId,
        hasReport: false,
        complianceScore: 0,
        totalPasses: 0,
        totalFailures: 0,
        totalWarnings: 0,
        totalErrors: 0,
        totalManual: 0,
        totalControls: 0,
        products: []
      }
    }
    throw error
  }
}

// User Insights APIs
export async function getUserInsights(tenantId: string): Promise<UserInsightsSummary | null> {
  try {
    const response = await apiClient.get<UserInsightsSummary>(`/userinsights/tenant/${tenantId}`)
    return response.data
  } catch (error: unknown) {
    // Return null if no insights data exists
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

export async function getUserInsightsLicenses(tenantId: string): Promise<LicenseSkuSummary[]> {
  try {
    const response = await apiClient.get<LicenseSkuSummary[]>(`/userinsights/tenant/${tenantId}/licenses`)
    return response.data
  } catch (error: unknown) {
    // Return empty array if no data exists
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return []
    }
    throw error
  }
}

export async function getUserInsightsUsers(
  tenantId: string,
  filter: UserIssueFilter = 'All',
  page: number = 1,
  limit: number = 50,
  /**
   * When `true`, narrows the result server-side to Global Admins under
   * the active filter. The Container honors `?globalAdminsOnly=true` via
   * a `[FromQuery]` model-binding (case-insensitive). The Function App
   * proxies the query string transparently.
   *
   * Sent only when truthy so URLs stay clean for the default unscoped
   * call path — avoids a behavior change for older log readers and keeps
   * the cache key stable on existing callers.
   */
  globalAdminsOnly?: boolean,
): Promise<UserDetailListResult> {
  try {
    const params: Record<string, string | number | boolean> = { filter, page, limit }
    if (globalAdminsOnly) {
      params.globalAdminsOnly = true
    }
    const response = await apiClient.get<UserDetailListResult>(
      `/userinsights/tenant/${tenantId}/users`,
      { params }
    )
    return response.data
  } catch (error: unknown) {
    // Return empty result if no data exists
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return {
        items: [],
        total: 0,
        page: 1,
        limit: 50,
        totalPages: 0
      }
    }
    throw error
  }
}

export async function refreshUserInsights(tenantId: string): Promise<RefreshResponse> {
  const response = await apiClient.post<RefreshResponse>(`/userinsights/tenant/${tenantId}/refresh`)
  return response.data
}

export async function getUserInsightsStatus(tenantId: string): Promise<RefreshStatusInfo> {
  const response = await apiClient.get<RefreshStatusInfo>(`/userinsights/tenant/${tenantId}/status`)
  return response.data
}

export async function getPhishResistantMfaSummary(tenantId: string): Promise<PhishResistantMfaSummary | null> {
  try {
    const response = await apiClient.get<PhishResistantMfaSummary>(
      `/userinsights/tenant/${tenantId}/phish-resistant-mfa`
    )
    return response.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

export async function getUserInsightsTrends(tenantId: string, days: number = 90): Promise<UserInsightsTrendsResult | null> {
  try {
    const response = await apiClient.get<UserInsightsTrendsResult>(
      `/userinsights/tenant/${tenantId}/trends`,
      { params: { days } }
    )
    return response.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

// Microsoft Secure Score APIs

export async function getSecureScoreSummary(tenantId: string): Promise<SecureScoreSummary | null> {
  try {
    const response = await apiClient.get<SecureScoreSummary>(`/securescore/tenant/${tenantId}/summary`)
    return response.data
  } catch (error: unknown) {
    // Return null if no data available or not configured
    if (axios.isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 500)) {
      console.warn('Secure Score data not available for tenant:', tenantId)
      return null
    }
    throw error
  }
}

export async function getSecureScore(tenantId: string): Promise<SecureScoreResult | null> {
  try {
    const response = await apiClient.get<SecureScoreResult>(`/securescore/tenant/${tenantId}`)
    return response.data
  } catch (error: unknown) {
    // Return null if no data available
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

export async function getSecureScoreRecommendations(tenantId: string): Promise<SecureScoreControlProfile[]> {
  try {
    const response = await apiClient.get<SecureScoreControlProfile[]>(`/securescore/tenant/${tenantId}/recommendations`)
    return response.data
  } catch (error: unknown) {
    // Return empty array if no recommendations available
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return []
    }
    throw error
  }
}

export async function refreshSecureScore(tenantId: string): Promise<SecureScoreSummary | null> {
  try {
    const response = await apiClient.post<SecureScoreSummary>(`/securescore/tenant/${tenantId}/refresh`)
    return response.data
  } catch (error: unknown) {
    // Return null if refresh fails
    if (axios.isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 500)) {
      console.warn('Failed to refresh Secure Score for tenant:', tenantId)
      return null
    }
    throw error
  }
}

export const insightsApi = {
  startScubaScan,
  getScubaReport,
  getScubaRisks,
  getScubaSummary,
  getUserInsights,
  getUserInsightsLicenses,
  getUserInsightsUsers,
  refreshUserInsights,
  getUserInsightsStatus,
  getPhishResistantMfaSummary,
  getUserInsightsTrends,
  getSecureScoreSummary,
  getSecureScore,
  getSecureScoreRecommendations,
  refreshSecureScore,
}
